import { useCallback, useEffect, useRef, useState } from 'react';
import type { Conversation, Section } from '@volt/shared';
import { useSession } from '../state/session.js';
import {
  cacheConversations,
  readCachedConversations,
  replaceCachedConversations,
} from '../lib/db.js';
import { filterVisibleConversations } from '../lib/sidebarInteractions.js';
import { useOnline } from './useOnline.js';

/** Why the list is served from IndexedDB rather than the server. */
export type ConversationsCacheReason = 'offline' | 'error';

export interface ConversationsState {
  conversations: Conversation[];
  loading: boolean;
  fromCache: boolean;
  /** `offline`: navigator.onLine is false; `error`: the request failed while online. */
  cacheReason: ConversationsCacheReason | null;
  /** More pages exist on the server (keyset pagination). */
  hasMore: boolean;
  loadingMore: boolean;
  /** Fetch the next page and append it (no-op while loading or when nothing is left). */
  loadMore: () => void;
  reload: () => void;
}

/** Rows per request; pinned chats are ordered first so they always land on page one. */
export const CONVERSATIONS_PAGE_SIZE = 100;

/**
 * Conversation list for the sidebar. Results are cached in IndexedDB and served
 * from cache while offline or when the request fails, so history stays visible.
 */
export function useConversations(
  section: Section,
  search: string,
  archived: boolean,
  version: number,
): ConversationsState {
  const { api } = useSession();
  const online = useOnline();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [cacheReason, setCacheReason] = useState<ConversationsCacheReason | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nonce, setNonce] = useState(0);
  // Identifies the query the loaded pages belong to, so a late page from a
  // previous query (other section / search) is discarded.
  const queryIdRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    const queryId = (queryIdRef.current += 1);
    setLoading(true);
    setNextCursor(null);
    loadingMoreRef.current = false;
    setLoadingMore(false);
    const query = search.trim();

    const serveCache = async (reason: ConversationsCacheReason) => {
      const cached = await readCachedConversations(section);
      if (cancelled) return;
      const filtered = query
        ? cached.filter((item) => item.title.toLowerCase().includes(query.toLowerCase()))
        : cached;
      setConversations(filterVisibleConversations(filtered, archived));
      setCacheReason(reason);
    };

    if (!online) {
      void serveCache('offline').finally(() => {
        if (!cancelled) setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }

    api
      .listConversations({
        section,
        search: query || undefined,
        archived,
        limit: CONVERSATIONS_PAGE_SIZE,
      })
      .then((response) => {
        if (cancelled || queryId !== queryIdRef.current) return;
        setConversations(filterVisibleConversations(response.conversations, archived));
        setCacheReason(null);
        setNextCursor(response.nextCursor);
        // A complete, unfiltered list is the truth for this section: prune rows the
        // server no longer returns. A partial page only upserts.
        if (!query && archived && response.nextCursor === null) {
          void replaceCachedConversations(section, response.conversations);
        } else {
          void cacheConversations(response.conversations);
        }
      })
      .catch(() => serveCache('error'))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, section, search, archived, version, nonce, online]);

  const loadMore = useCallback(() => {
    if (!nextCursor || loadingMoreRef.current || !online) return;
    const queryId = queryIdRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    api
      .listConversations({
        section,
        search: search.trim() || undefined,
        archived,
        limit: CONVERSATIONS_PAGE_SIZE,
        cursor: nextCursor,
      })
      .then((response) => {
        if (queryId !== queryIdRef.current) return;
        setConversations((prev) => {
          const known = new Set(prev.map((item) => item.id));
          const fresh = response.conversations.filter((item) => !known.has(item.id));
          return [...prev, ...filterVisibleConversations(fresh, archived)];
        });
        setNextCursor(response.nextCursor);
        void cacheConversations(response.conversations);
      })
      .catch(() => undefined)
      .finally(() => {
        if (queryId !== queryIdRef.current) return;
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [api, section, search, archived, nextCursor, online]);

  return {
    conversations,
    loading,
    fromCache: cacheReason !== null,
    cacheReason,
    hasMore: nextCursor !== null,
    loadingMore,
    loadMore,
    reload,
  };
}
